import {
  businessAccount,
  businessPayments,
  fmtEur,
  splitAmount,
  virtualCards,
} from "@/shared/data";
import { icon } from "@/shared/icons";
import type { Route } from "@/shared/layout";

interface Ctx {
  canvas: HTMLElement;
  navigate: (r: Route) => void;
}

export function renderBusiness(ctx: Ctx) {
  const { whole, frac } = splitAmount(businessAccount.available);
  const recent = businessPayments.slice(0, 6);
  const liveCardsTotal = virtualCards
    .filter((c) => c.status === "active")
    .reduce((a, c) => a + c.spent30d, 0);

  ctx.canvas.innerHTML = `
    <section class="page-head">
      <span class="page-head-watermark"></span>
      <div>
        <span class="page-eyebrow">Business Account<span class="page-eyebrow-rule"></span>Overview</span>
        <h1 class="page-title">${businessAccount.accountName}</h1>
      </div>
      <div class="page-head-meta" style="flex-direction: row; align-items: center; gap: 0.6rem;">
        <button class="btn btn-ghost btn-sm">Download statement ${icon("download", 11)}</button>
        <button class="btn btn-primary" data-route="send">${icon("plus", 11)} New payment</button>
      </div>
    </section>

    <section class="ba-headline">
      <div class="ba-headline-amount">
        <div class="ba-headline-label">Available balance</div>
        <div class="ba-headline-value">
          <span class="ba-headline-cur">€</span><span>${whole}</span><span class="ba-headline-frac">${frac}</span>
        </div>
        <div class="ba-headline-foot">
          <span style="color: var(--state-success); font-weight: 700;">↑ ${fmtEur(481_200, { compact: true })}</span> · last settlement 03 May
        </div>
      </div>
      <div class="ba-headline-coords">
        <div class="ba-coord">
          <span class="ba-coord-label">IBAN</span>
          <span class="ba-coord-value">${businessAccount.iban}</span>
          <button class="ba-coord-copy" type="button" aria-label="Copy IBAN">${icon("copy", 12)}</button>
        </div>
        <div class="ba-coord">
          <span class="ba-coord-label">BIC / SWIFT</span>
          <span class="ba-coord-value">${businessAccount.bic}</span>
          <button class="ba-coord-copy" type="button" aria-label="Copy BIC">${icon("copy", 12)}</button>
        </div>
        <div class="ba-coord">
          <span class="ba-coord-label">Bank</span>
          <span class="ba-coord-value">${businessAccount.bank}</span>
        </div>
      </div>
    </section>

    <section class="summary-row">
      <div class="summary-cell">
        <span class="summary-cell-label">Pending outgoing</span>
        <span class="summary-cell-value">${fmtEur(businessAccount.pendingOutgoing, { compact: true })}</span>
        <span class="summary-cell-note">3 SEPA · 1 SWIFT</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Outflow · 30d</span>
        <span class="summary-cell-value">${fmtEur(businessAccount.monthlyOutflow, { compact: true })}</span>
        <span class="summary-cell-note">All rails combined</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Card spend · 30d</span>
        <span class="summary-cell-value">${fmtEur(liveCardsTotal, { compact: true })}</span>
        <span class="summary-cell-note">${virtualCards.filter((c) => c.status === "active").length} active virtual cards</span>
      </div>
      <div class="summary-cell">
        <span class="summary-cell-label">Next payroll</span>
        <span class="summary-cell-value">€61.4k</span>
        <span class="summary-cell-note">14 employees · 30 May</span>
      </div>
    </section>

    <section class="grid-2-1">
      <article class="panel">
        <div class="panel-head">
          <div>
            <div class="panel-eyebrow">Recent activity</div>
            <h2 class="panel-title">Business account movement</h2>
            <p class="panel-sub">Payments, settlements and card charges from the past week.</p>
          </div>
          <a class="link-arrow" data-route="business-statements">All statements ${icon("arrowRight", 13)}</a>
        </div>
        <div class="ledger-table">
          <div class="ledger-table-header" style="grid-template-columns: 90px minmax(0, 1.6fr) 110px minmax(0, 130px) 110px;">
            <span>Date / Time</span>
            <span>Beneficiary / Reference</span>
            <span>Rail</span>
            <span style="text-align: right;">Amount</span>
            <span>Status</span>
          </div>
          ${recent
            .map(
              (p) => `<div class="ledger-table-row" style="grid-template-columns: 90px minmax(0, 1.6fr) 110px minmax(0, 130px) 110px;">
            <div class="col-date">
              <strong>${p.date}</strong>
              ${p.time}
            </div>
            <div class="col-name" style="min-width: 0;">
              <strong>${p.beneficiary}</strong>
              <span>${p.reference}</span>
            </div>
            <div class="col-method">
              <span class="col-method-icon ${p.rail === "sepa" ? "" : p.rail === "swift" ? "is-card" : p.rail === "internal" ? "is-wallet" : "is-card"}">${p.rail[0]?.toUpperCase()}</span>
              ${p.railLabel}
            </div>
            <div class="col-amount is-${p.direction}">
              ${p.direction === "in" ? "+" : "−"}${fmtEur(p.amountEur)}
              ${p.amountText ? `<span class="col-amount-sub">${p.amountText}</span>` : ""}
            </div>
            <div class="col-status">
              <span class="status-seal is-${p.status}"></span> ${p.statusLabel}
            </div>
          </div>`,
            )
            .join("")}
        </div>
      </article>

      <article class="actions-panel">
        <span class="actions-panel-watermark" aria-hidden="true"></span>
        <span class="actions-panel-eyebrow">Quick actions</span>
        <h2 class="actions-panel-title">Move <em>operational</em> money.</h2>
        <p class="actions-panel-sub">Send to a saved beneficiary, queue a payroll run, or issue a virtual card.</p>

        <div class="actions-list">
          <button class="action-key" data-route="send">
            <span class="action-key-num"><strong>I.</strong></span>
            <span class="action-key-body">
              <strong>Send · SEPA / SWIFT</strong>
              <span>Single external payment</span>
            </span>
            <span class="action-key-arrow">→</span>
          </button>
          <button class="action-key" data-route="payroll">
            <span class="action-key-num"><strong>II.</strong></span>
            <span class="action-key-body">
              <strong>Run payroll</strong>
              <span>Upload CSV · 14 employees due 30 May</span>
            </span>
            <span class="action-key-arrow">→</span>
          </button>
          <button class="action-key" data-route="cards">
            <span class="action-key-num"><strong>III.</strong></span>
            <span class="action-key-body">
              <strong>Issue virtual card</strong>
              <span>For ad spend, subscriptions, vendors</span>
            </span>
            <span class="action-key-arrow">→</span>
          </button>
          <button class="action-key" data-route="beneficiaries">
            <span class="action-key-num"><strong>IV.</strong></span>
            <span class="action-key-body">
              <strong>Manage beneficiaries</strong>
              <span>Saved IBANs and SWIFT recipients</span>
            </span>
            <span class="action-key-arrow">→</span>
          </button>
        </div>
      </article>
    </section>
  `;

  ctx.canvas.querySelectorAll<HTMLElement>("[data-route]").forEach((el) => {
    el.addEventListener("click", (e) => {
      e.preventDefault();
      const route = el.getAttribute("data-route") as Route;
      if (route) ctx.navigate(route);
    });
  });

  // Copy IBAN / BIC — visual feedback
  ctx.canvas
    .querySelectorAll<HTMLButtonElement>(".ba-coord-copy")
    .forEach((btn) => {
      btn.addEventListener("click", () => {
        const value =
          btn.previousElementSibling?.textContent?.trim() ?? "";
        if (value) navigator.clipboard?.writeText(value).catch(() => {});
        btn.classList.add("is-copied");
        setTimeout(() => btn.classList.remove("is-copied"), 1500);
      });
    });
}
